import React from 'react';
import SEO from '../components/SEO';
import CTABanner from '../components/CTABanner';
import Footer from '../components/Footer';
import { ArrowUpRight, BookOpen } from 'lucide-react';

const featured = {
  title: 'The 9-step newsletter monetization framework I run for every retainer client',
  excerpt:
    'A no-fluff walkthrough of the framework that turns a free newsletter into a recurring revenue line — without burning your list out.',
  category: 'Frameworks',
  readTime: '12 min read',
  date: 'Feb 2026',
  image: 'https://images.unsplash.com/photo-1579170053380-58828ce5f6e8?w=1400&q=80',
};

const posts = [
  {
    title: 'Why your Kit.com migration is failing (and the boring fix)',
    excerpt: 'Tag mapping, double-enrolment loops, and the deliverability tax most consultants miss.',
    category: 'Deliverability',
    readTime: '8 min read',
    date: 'Jan 2026',
    image: 'https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=900&q=80',
  },
  {
    title: '3 subject-line patterns that beat clever copywriting every time',
    excerpt: 'Real opens from real campaigns — and the data behind why specificity always wins.',
    category: 'Copywriting',
    readTime: '6 min read',
    date: 'Jan 2026',
    image: 'https://images.unsplash.com/photo-1556761175-4b46a572b786?w=900&q=80',
  },
  {
    title: 'Building a podcast → newsletter funnel that actually compounds',
    excerpt: 'The exact tag schema, broadcast cadence, and conversion loop I built for The Well Drop.',
    category: 'Funnels',
    readTime: '10 min read',
    date: 'Dec 2025',
    image: 'https://images.unsplash.com/photo-1485579149621-3123dd979885?w=900&q=80',
  },
  {
    title: 'ManyChat + Kit.com: the cleanest 2-channel sync I\'ve found',
    excerpt: 'Step-by-step automation map, error handling, and how to measure what each channel earned.',
    category: 'Automation',
    readTime: '9 min read',
    date: 'Dec 2025',
    image: 'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=900&q=80',
  },
  {
    title: 'Your "open rate" is lying. Here is what to track instead.',
    excerpt: 'Why iOS MPP broke open rates, and the four engagement signals I now report to clients.',
    category: 'Reporting',
    readTime: '7 min read',
    date: 'Nov 2025',
    image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=900&q=80',
  },
  {
    title: 'When to fire your ESP — a 5-question diagnostic',
    excerpt: 'Migrations are expensive. Use this checklist before booking the call.',
    category: 'Strategy',
    readTime: '5 min read',
    date: 'Nov 2025',
    image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=900&q=80',
  },
];

const Blog = () => {
  return (
    <>
      <SEO
        title="Field Notes — Email Marketing Essays | Growth Hacking"
        description="Long-form notes on deliverability, Kit.com, ActiveCampaign, newsletter monetization and the boring infrastructure that makes campaigns compound."
        canonical="/blog"
      />
      <main className="bg-cream pt-28">
        {/* Hero */}
        <section className="container-x section pt-12" data-testid="blog-hero">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-cream-2 border border-line text-xs font-semibold uppercase tracking-[0.2em] text-ink">
                Field notes
              </span>
              <h1 className="font-display text-5xl md:text-7xl font-extrabold mt-6 leading-[0.98] text-ink tracking-tighter">
                Long-form on the{' '}
                <span className="font-serif italic text-[var(--gh-orange)]">boring layer</span>{' '}
                that makes inboxes work.
              </h1>
            </div>
            <p className="lg:col-span-4 text-lg text-muted-warm">
              No clickbait, no list-icles. Notes I&apos;d send to a friend running their own
              email program.
            </p>
          </div>
        </section>

        {/* Featured */}
        <section className="container-x section pt-0" data-testid="blog-featured">
          <article className="grid lg:grid-cols-12 gap-8 items-stretch group">
            <div className="lg:col-span-7 rounded-[1.75rem] overflow-hidden bg-cream-2 border border-line">
              <img
                src={featured.image}
                alt={featured.title}
                className="w-full h-[420px] object-cover group-hover:scale-105 transition-transform duration-700"
              />
            </div>
            <div className="lg:col-span-5 rounded-[1.75rem] bg-cream border border-line p-10 flex flex-col justify-between">
              <div>
                <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-muted-warm font-semibold">
                  <span className="px-2.5 py-1 rounded-full bg-[var(--gh-orange-soft)] text-[var(--gh-orange)]">
                    {featured.category}
                  </span>
                  <span>{featured.date}</span>
                  <span>·</span>
                  <span>{featured.readTime}</span>
                </div>
                <h2 className="font-display text-3xl md:text-4xl font-extrabold mt-6 text-ink leading-tight">
                  {featured.title}
                </h2>
                <p className="mt-4 text-base text-muted-warm leading-relaxed">{featured.excerpt}</p>
              </div>
              <a
                href="#" data-testid="blog-featured-link"
                className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-ink hover:text-[var(--gh-orange)] transition-colors"
              >
                Read the full essay <ArrowUpRight className="w-4 h-4" />
              </a>
            </div>
          </article>
        </section>

        {/* Grid */}
        <section className="container-x section pt-0" data-testid="blog-grid">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map((p, i) => (
              <article
                key={p.title}
                data-testid={`blog-card-${i}`}
                className="card-cream overflow-hidden flex flex-col"
              >
                <div className="aspect-[16/10] overflow-hidden bg-cream-2">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-muted-warm font-semibold">
                    <span className="px-2 py-0.5 rounded-full bg-[var(--gh-orange-soft)] text-[var(--gh-orange)]">
                      {p.category}
                    </span>
                    <span>{p.date}</span>
                  </div>
                  <h3 className="font-display text-xl font-bold mt-4 text-ink leading-snug">{p.title}</h3>
                  <p className="mt-3 text-sm text-muted-warm leading-relaxed flex-1">{p.excerpt}</p>
                  <span className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-ink">
                    <BookOpen className="w-4 h-4" /> {p.readTime}
                  </span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <CTABanner
          eyebrow="Stay in the loop"
          title="One short field note. Every other Thursday."
          body="Subscribe at the bottom of the page — no fluff, no resharing of LinkedIn takes. Just whatever I'm shipping for clients that week."
        />
      </main>
      <Footer />
    </>
  );
};

export default Blog;
